import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {Col, Label, Row} from "reactstrap";
import "./eventPreReleaseEdit.sass";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {useTranslation} from "react-i18next";
import {addDays, addHours} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import FadeContent from "../../molecules/fadeContent/fadeContent";
import {useStores} from "../../../stores/useStores";
import DateEdit from "../dateEdit/dateEdit";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import {oneDayInMilliseconds, recurrenceOnce} from "../../../shared/constants";
import EventDetails from "../../../stores/domainStore/pages/models/eventDetails";
import {runInAction} from "mobx";

export interface IEventPreReleaseEditProps {
  value: DateObservable;
  eventDetails: EventDetails;
  isEditing: boolean;
}

const EventPreReleaseEdit: React.FC<IEventPreReleaseEditProps> = ({
  value,
  eventDetails,
  isEditing
}) => {
  const {t} = useTranslation(["event"]);
  const {
    uiStore: {eventsUiStore}
  } = useStores();

  const [isEventReleased, setIsEventReleased] = useState(false);
  const [requiresIndependentRelease, setRequiresIndependentRelease] =
    useState(false);

  useEffect(() => {
    async function checkIndependentRelease() {
      if (
        eventDetails.eventDateDetails.startDate?.date &&
        eventDetails.bookingDetails.hasBookingDeadline
      ) {
        let eventScheduleNextReleaseDate =
          eventsUiStore.calendarSchedule?.releaseDate;
        if (eventScheduleNextReleaseDate == null) {
          await eventsUiStore.loadEventReleaseDate();
          eventScheduleNextReleaseDate =
            eventsUiStore.calendarSchedule!.releaseDate;
        }

        // Period of time the calendar is released before the start of the month
        const eventScheduleReleaseTimePeriod =
          new Date(
            eventScheduleNextReleaseDate!.getFullYear(),
            eventScheduleNextReleaseDate!.getMonth() + 1,
            1
          ).getTime() - eventScheduleNextReleaseDate!.getTime();

        // When the event will be released
        const eventScheduleReleaseDate =
          new Date(
            eventDetails.eventDateDetails.startDate.date.getFullYear(),
            eventDetails.eventDateDetails.startDate.date.getMonth(),
            1
          ).getTime() - eventScheduleReleaseTimePeriod;

        const currentTime = utcToZonedTime(
          new Date(),
          eventDetails.timeZone
        ).getTime();

        const isReleased =
          ((value.date &&
            utcToZonedTime(value.date, eventDetails.timeZone).getTime()) ??
            eventScheduleReleaseDate) <= currentTime;
        setIsEventReleased(isReleased);

        if (
          !eventDetails.isRecurringEvent &&
          eventDetails.eventRecurrenceDetails.frequency === recurrenceOnce &&
          (!isReleased || value.date)
        ) {
          if (isReleased) {
            setRequiresIndependentRelease(true);
            return;
          }
          const daysAbleToBook =
            (eventDetails.deadlineClosedDate!.getTime() -
              eventScheduleReleaseDate) /
            oneDayInMilliseconds;
          // Independent release required if have less than 3 days to book after calendar released
          if (daysAbleToBook < 3) {
            if (!value.date) {
              eventDetails.setReleaseDateFromBookingDeadline(true);
            }
            setRequiresIndependentRelease(true);
            return;
          }
        }
      }
      // If independent release not required, reset releaseDate to null
      if (value.date) {
        runInAction(() => {
          value.date = null;
        });
      }
      setRequiresIndependentRelease(false);
      return;
    }

    checkIndependentRelease();
  }, [
    eventDetails.eventDateDetails.startDate,
    eventDetails.bookingDetails.hasBookingDeadline,
    eventDetails.deadlineClosedDate,
    eventDetails.eventRecurrenceDetails.frequency
  ]);

  return (
    <FadeContent fadeContent={!requiresIndependentRelease}>
      <div className="preRelease-edit-field">
        <Row>
          <Col>
            <Row>
              <div className="published-icon">
                <Icon source={IconSource.published} />
              </div>
              <Label className="edit-preRelease-label">
                {t("event:labels:preReleaseEvent")}
              </Label>
            </Row>

            <Row>
              <Label className="edit-help-text">
                {t("event:labels:preReleaseHelpText")}
              </Label>
            </Row>
          </Col>
          <Col>
            <DateEdit
              label={t("event:labels:preReleaseDate")}
              value={value}
              dateFormat={"dd/MM/yyyy hh:mm a"}
              validationMessage={t("event:errors:preReleaseDate")}
              openToDate={value.date}
              disabled={isEventReleased && isEditing && !value.isDirty}
              timeZone={eventDetails.timeZone}
              minDate={utcToZonedTime(
                addHours(new Date(), 1),
                eventDetails.timeZone
              )}
              maxDate={addDays(eventDetails.deadlineClosedDate!, -3)}
            />
          </Col>
        </Row>
      </div>
    </FadeContent>
  );
};

export default observer(EventPreReleaseEdit);
